import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Modal,
  TextField,
  Typography
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { deleteCustomer, getCustomer, patchCustomer } from '../../api/customersAPI';
import { createCustomerVehicle, createVehicle, deleteCustomerVehicle, getVehicles } from '../../api/vehiclesAPI';
import { formatMileage } from '../../utils/dateUtils';
import { formatPhoneNum } from "../../utils/phoneUtils";
import NavBar from '../layout/NavBar';
import ServiceModal from "../modals/ServiceModal";

export default function ViewCustomer() {
  // Get ID of customer from URL
  let { id } = useParams();

  // Customer information from fetch call
  const [customerInfo, setCustomerInfo] = useState([]);

  // Track modal state for the edit user modal
  const [openUser, setOpenUser] = useState(false);
  const handleOpenUser = () => setOpenUser(true);
  const handleCloseUser = () => setOpenUser(false);

  // Track modal state for the new vehicle modal
  const [openVehicle, setOpenVehicle] = useState(false);
  const handleOpenVehicle = () => setOpenVehicle(true);
  const handleCloseVehicle = () => setOpenVehicle(false);

  // Track modal state for vehicle linking modal
  const [openVehicleLink, setOpenVehicleLink] = useState(false);
  const handleOpenVehicleLink = () => setOpenVehicleLink(true);
  const handleCloseVehicleLink = () => setOpenVehicleLink(false);

  // Track modal state for vehicle unlinking modal
  const [openVehicleUnlink, setOpenVehicleUnlink] = useState(false);
  const handleOpenVehicleUnlink = () => setOpenVehicleUnlink(true);
  const handleCloseVehicleUnlink = () => setOpenVehicleUnlink(false);

  // Track vehicle that has the add service button clicked
  const [targetVehicle, setTargetVehicle] = useState(null);

  // Track modal state for the add service modal
  const [openService, setOpenService] = useState(false);
  const handleOpenService = (vehicleId) => {
    setTargetVehicle(vehicleId);
    setOpenService(true);
  }
  const handleCloseService = () => {
    setOpenService(false);
    setTargetVehicle(null);
  };

  // Set empty customer form values which will be populated by the fetched customer data for editing customer
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [taxExempt, setTaxExempt] = useState(false);
  const [acceptsTexts, setAcceptsTexts] = useState(false);
  const [acceptsEmails, setAcceptsEmails] = useState(false);
  const [isFlagged, setIsFlagged] = useState(false);
  const [customerNotes, setCustomerNotes] = useState('');

  // Set empty vehicle form values for adding a new vehicle
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [color, setColor] = useState('');
  const [license, setLicense] = useState('');
  const [vin, setVin] = useState('');
  const [notes, setNotes] = useState('');

  // Hold all vehicles for linking purposes
  const [vehicles, setVehicles] = useState([]);

  // Hold customer's vehicles for unlinking purposes
  const [customerVehicles, setCustomerVehicles] = useState([]);

  // Holds the vehicle that is attempting to be linked to a customer
  const [vehicleLink, setVehicleLink] = useState('');

  // Add error states
  const [editUserErrors, setEditUserErrors] = useState({});
  const [addVehicleErrors, setAddVehicleErrors] = useState({});

  /** DELETE vehicle and redirect to customers page. */
  const handleCustomerDelete = async () => {
    try {
      // Verify deletion of vehicle
      const confirmation = window.confirm('Are you sure you want to delete this customer?\nThis action cannot be undone.');
      if (!confirmation) {
        return;
      }
      await deleteCustomer(id);
      window.location = process.env.REACT_APP_BASE_URL + 'customers';
    } catch (error) {
      console.error(error);
    }
  };

  function makeVehicleDescription(vehicle) {
    let id = vehicle.id;
    let color = vehicle.color ? vehicle.color : '';
    let year = vehicle.year ? vehicle.year : ''
    let make = vehicle.make ? vehicle.make : '';
    let model = vehicle.model ? vehicle.model : '';
    let license = vehicle.license ? vehicle.license : '';
    let otherOwners = vehicle.list_owners_str ? '| ' + vehicle.list_owners_str : '| No customers associated';
    return `(ID: ${id}) ${color} ${year} ${make} ${model} ${license} ${otherOwners}`
  }

  /** Make GET request using ID from URL param to grab customer data */
  const getCustomerInfo = useCallback(async () => {
    try {
      const response = await getCustomer(id);
      const customerData = response.data;

      // Store customer data from response
      setCustomerInfo(customerData);

      // Set initial values for 'Edit User' form
      setFirstName(customerData.first_name);
      setLastName(customerData.last_name);
      setPhoneNumber(customerData.phone);
      setEmail(customerData.email);
      setTaxExempt(customerData.tax_exempt);
      setAcceptsTexts(customerData.accepts_texts);
      setAcceptsEmails(customerData.accepts_emails);
      setIsFlagged(customerData.flagged);
      setCustomerNotes(customerData.notes);

      customerData.vehicles && setCustomerVehicles(customerData.vehicles.map(vehicle => makeVehicleDescription(vehicle)));

    } catch (error) {
      console.error(error);
    }
  }, [id]);

  /** Make PATCH request to update customer data */
  const handleUpdateCustomer = async () => {
    let values = {
      'business_id': customerInfo.business,
      'first_name': firstName,
      'last_name': lastName,
      'phone': phoneNumber,
      'email': email,
      'tax_exempt': taxExempt,
      'accepts_emails': acceptsEmails,
      'accepts_texts': acceptsTexts,
      'flagged': isFlagged,
      'notes': customerNotes,
    };

    try {
      await patchCustomer(id, values);
      handleCloseUser();
      getCustomerInfo();
      setEditUserErrors({});
    } catch (error) {
      console.error(error);
      if (error.response && error.response.data) {
        setEditUserErrors(error.response.data);
      }
    }
  };

  /** Make POST request to add new vehicle for a customer */
  const addVehicle = async () => {
    // Combine 'Add Vehicle' form values to use in POST request
    let values = {
      'make': make,
      'model': model,
      'year': year,
      'color': color,
      'license': license,
      'vin': vin,
      'notes': notes,
    };

    // Try to create a new vehicle
    try {
      let response = await createVehicle(values);

      let user_id = customerInfo.id;
      let new_vehicle_id = response.data.id;

      let customer_vehicle_values = {
        'customer': user_id,
        'vehicle': new_vehicle_id
      }

      // Associate vehicle with customer
      await createCustomerVehicle(customer_vehicle_values)

      // Close modal
      handleCloseVehicle();

      // Refresh customer info
      getCustomerInfo();
      // TODO: Cleanup individual values
      setMake('');
      setModel('');
      setYear('');
      setColor('');
      setLicense('');
      setVin('');
      setNotes('');
      setAddVehicleErrors({});
    } catch (error) {
      console.error(error);
      if (error.response && error.response.data) {
        setAddVehicleErrors(error.response.data);
      }
    }
  }

  /** Make POST request to link existing vehicle to existing customer */
  const linkVehicle = async () => {
    let vehicleID = parseInt(vehicleLink.split(' ')[1].slice(0, -1));
    let customerID = parseInt(customerInfo.id);
    let values = {
      'customer': customerID,
      'vehicle': vehicleID
    };

    try {
      await createCustomerVehicle(values);
      // Close modal
      handleCloseVehicleLink();

      // Refresh customer info
      getCustomerInfo();
    } catch (error) {
      console.error(error);
    }
  }

  /** Make DELETE request to unlink vehicle to customer */
  const unlinkVehicle = async () => {
    let customerID = parseInt(customerInfo.id);
    let vehicleID = parseInt(vehicleLink.split(' ')[1].slice(0, -1));

    try {
      await deleteCustomerVehicle(customerID, vehicleID);

      // Close modal
      handleCloseVehicleUnlink();

      // Refresh customer info
      getCustomerInfo();
    } catch (error) {
      console.error(error);
    }
  }

   /** Make GET request to get all vehicle data */
  const handleGetVehicles = useCallback(async () => {
    // Try to get all vehicles
    try {
      let response = await getVehicles();
      let rawVehicleList = response.data;
      setVehicles(rawVehicleList.map(vehicle => makeVehicleDescription(vehicle)));
    } catch (error) {
      console.error(error);
    }
  }, []);

  // Get customer info
  useEffect(() => {
    getCustomerInfo();
    handleGetVehicles();
  }, [getCustomerInfo, handleGetVehicles, openVehicle, openVehicleLink, openVehicleUnlink]);

  const serviceOwners = useMemo(() => {
    if (!customerInfo?.id) {
      return [];
    }

    return [{
      id: customerInfo.id,
      name: `${customerInfo.first_name ?? ''} ${customerInfo.last_name ?? ''}`.trim(),
      tax_exempt: customerInfo.tax_exempt ?? false,
    }];
  }, [customerInfo]);

  // Custom styles
    const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 600,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
  };

  const red_style = (x) => {
    return {
      fontWeight: x? 'bold' : 'normal',
      color: x? '#fff' : 'inherit',
      backgroundColor: x? '#d32f2f' : 'transparent',
      borderRadius: x? 2 : 0,
      px: x? 1.5 : 0,
      py: x ? 0.5 : 0,
      display: 'inline-block',
      boxShadow: x? '0 2px 8px rgba(211,47,47,0.15)' : 'none',
      letterSpacing: x? 1 : 'normal',
      transition: 'all 0.2s'
    }
  }

  return (
    <Box sx={{
      textAlign: 'center',
      minWidth: '519px',
      maxWidth: '75%',
      margin: 'auto' }}
    >
      <Typography variant='h2'>Customer Detail</Typography>
      <NavBar active='Customers' />
      <br/>
      <Typography><strong>{customerInfo.first_name} {customerInfo.last_name}</strong></Typography>
      <br/>
      <Box sx={{ display:'Flex', justifyContent:'center', gap: '5px' }}>
        <Button variant='outlined' onClick={handleOpenUser}>Edit</Button>
        <Button variant='outlined' color='error' onClick={handleCustomerDelete} >Delete</Button>
      </Box>
      {/* Modal start for editing customer */}
      <Modal
        open={openUser}
        onClose={handleCloseUser}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Edit User
          </Typography>
          <FormGroup>
            <TextField
              required id='first-name'
              label='First Name'
              variant='outlined'
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              sx={{ mb: 1 }}
              error={!!editUserErrors.first_name}
              helperText={editUserErrors.first_name}
            />
            <TextField
              required id='last-name'
              label='Last Name'
              variant='outlined'
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              sx={{ mb: 1 }}
              error={!!editUserErrors.last_name}
              helperText={editUserErrors.last_name}
            />
            <TextField
              required id='phone-number'
              label='Phone Number'
              variant='outlined'
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              sx={{ mb: 1 }}
              error={!!editUserErrors.phone}
              helperText={editUserErrors.phone}
            />
            <TextField
              id='email'
              label='Email'
              variant='outlined'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 1 }}
              error={!!editUserErrors.email}
              helperText={editUserErrors.email}
            />
            <FormControlLabel
              control={<Checkbox />}
              label='Tax Exempt'
              checked={taxExempt}
              onChange={(e) => setTaxExempt(!taxExempt)}
            />
            <FormControlLabel
              control={<Checkbox />}
              label='Accepts Texts'
              checked={acceptsTexts}
              onChange={(e) => setAcceptsTexts(!acceptsTexts)}
            />
            <FormControlLabel
              control={<Checkbox />}
              label='Accepts Emails'
              checked={acceptsEmails}
              onChange={(e) => setAcceptsEmails(!acceptsEmails)}
            />
            <FormControlLabel
              control={<Checkbox />}
              label='Flagged'
              checked={isFlagged}
              onChange={(e) => setIsFlagged(!isFlagged)}
            />
            <TextField
              id='customer-notes'
              label='Customer Notes'
              variant='outlined'
              multiline
              rows={4}
              value={customerNotes}
              onChange={(e) => setCustomerNotes(e.target.value)}
              sx={{ mb: 1 }}
              error={!!editUserErrors.customer_notes}
              helperText={editUserErrors.customer_notes}
            />
            <Button variant='contained' onClick={handleUpdateCustomer}>Submit</Button>
          </FormGroup>
        </Box>
      </Modal>
      {/* Modal end for editing customer */}

      {/* Modal start for adding vehicle */}
      <Modal
        open={openVehicle}
        onClose={handleCloseVehicle}
        aria-labelledby="vehicle-modal-title"
        aria-describedby="vehicle-modal-description"
      >
        <Box sx={style}>
          <Typography id="vehicle-modal-title" variant="h6" component="h2">
            Add Vehicle
          </Typography>
          <FormGroup>
            <TextField
              required
              id='make'
              label='Make'
              variant='outlined'
              value={make}
              onChange={(e) => setMake(e.target.value)}
              sx={{ mb: 1 }}
              error={!!addVehicleErrors.make}
              helperText={addVehicleErrors.make}
            />
            <TextField
              required
              id='model'
              label='Model'
              variant='outlined'
              value={model}
              onChange={(e) => setModel(e.target.value)}
              sx={{ mb: 1 }}
              error={!!addVehicleErrors.model}
              helperText={addVehicleErrors.model}
            />
            <TextField
              required
              id='year'
              label='Year'
              variant='outlined'
              value={year}
              onChange={(e) => setYear(e.target.value)}
              sx={{ mb: 1 }}
              error={!!addVehicleErrors.year}
              helperText={addVehicleErrors.year}
            />
            <TextField
              id='color'
              label='Color'
              variant='outlined'
              value={color}
              onChange={(e) => setColor(e.target.value)}
              sx={{ mb: 1 }}
              error={!!addVehicleErrors.color}
              helperText={addVehicleErrors.color}
            />
            <TextField
              id='license'
              label='License'
              variant='outlined'
              value={license}
              onChange={(e) => setLicense(e.target.value)}
              sx={{ mb: 1 }}
              error={!!addVehicleErrors.license}
              helperText={addVehicleErrors.license}
            />
            <TextField
              id='vin'
              label='VIN'
              variant='outlined'
              value={vin}
              onChange={(e) => setVin(e.target.value)}
              sx={{ mb: 1 }}
              error={!!addVehicleErrors.vin}
              helperText={addVehicleErrors.vin}
            />
            <TextField
              id='notes'
              label='Vehicle Notes'
              variant='outlined'
              multiline
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              sx={{ mb: 1 }}
              error={!!addVehicleErrors.notes}
              helperText={addVehicleErrors.notes}
            />
            <Button variant='contained' onClick={addVehicle}>Submit</Button>
          </FormGroup>
        </Box>
      </Modal>
      {/* Modal end for adding vehicle */}

      {/* Modal start for linking vehicle */}
      <Modal
        open={openVehicleLink}
        onClose={handleCloseVehicleLink}
        aria-labelledby="vehicle-link-modal-title"
        aria-describedby="vehicle-link-modal-description"
      >
        <Box sx={style}>
          <Typography id="vehicle-link-modal-title" variant="h6" component="h2">
            Link Vehicle
          </Typography>
          <FormGroup>
            <Autocomplete
              slotProps={{
                listbox: { style: { maxHeight: 250, overflow: 'auto' }}
              }}
              sx={{ my: 2 }}
              id="free-solo-demo"
              options={vehicles.map((option) => option)}
              renderInput={(params) => <TextField {...params} label="Existing Vehicles" />}
              onChange={(e, v) => {setVehicleLink(v)}}
            />
            <Button variant='contained' onClick={linkVehicle}>Submit</Button>
          </FormGroup>
        </Box>
      </Modal>
      {/* Modal end for linking vehicle */}

      {/*Modal start for unlinking vehicle*/}
      <Modal
        open={openVehicleUnlink}
        onClose={handleCloseVehicleUnlink}
        aria-labelledby="vehicle-unlink-modal-title"
        aria-describedby="vehicle-unlink-modal-description"
      >
        <Box sx={style}>
          <Typography id="vehicle-unlink-modal-title" variant="h6" component="h2">
            Unlink Vehicle
          </Typography>
          <FormGroup>
            <Autocomplete
              slotProps={{
                listbox: { style: { maxHeight: 250, overflow: 'auto' }}
              }}
              sx={{ my: 2 }}
              id="free-solo-demo"
              options={customerVehicles && customerVehicles.map((option) => option)}
              renderInput={(params) => <TextField {...params} label="Existing Vehicles" />}
              onChange={(e, v) => {setVehicleLink(v)}}
            />
            <Button variant='contained' onClick={unlinkVehicle}>Submit</Button>
          </FormGroup>
        </Box>
      </Modal>
      {/*Modal end for unlinking vehicle*/}

      <br/>
      <Typography sx={red_style(customerInfo.tax_exempt)}>Tax Exempt: {customerInfo.tax_exempt ? 'Yes' : 'No'}</Typography>
      <br/>
      <br/>
      <Typography>{formatPhoneNum(customerInfo.phone)}</Typography>
      <Typography>Accepts Texts: {customerInfo.accepts_texts ? 'Yes' : 'No'}</Typography>
      <br/>
      <Typography>{customerInfo.email}</Typography>
      <Typography>Accepts Emails: {customerInfo.accepts_emails ? 'Yes' : 'No'}</Typography>
      <br/>
      <Typography
        sx={red_style(customerInfo.flagged)}
      >
        Flagged: {customerInfo.flagged ? 'Yes' : 'No'}
      </Typography>
      <Typography>Notes: {customerInfo.notes ? customerInfo.notes : 'None'}</Typography>
      <br/>
      <Typography variant='h4'>Vehicles</Typography>
      <br/>
      <Button sx={{ width: 225, mx: 1 }} variant='outlined' onClick={handleOpenVehicle}>Add New Vehicle</Button>
      <Button sx={{ width: 225, mx: 1 }} variant='outlined' onClick={handleOpenVehicleLink}>Link Existing Vehicle</Button>
      <Button sx={{ width: 225, mx: 1 }} variant='outlined' onClick={handleOpenVehicleUnlink}>Unlink Vehicle</Button>
      <br/>
      <br/>

      <ServiceModal
        isOpen={openService}
        onClose={handleCloseService}
        vehicleId={targetVehicle}
        owners={serviceOwners}
        refreshFunction={getCustomerInfo}
      />

      <Box sx={{ display: 'flex', justifyContent: 'center', gap: '15px', flexWrap:'wrap'}}>
       {customerInfo.vehicles && customerInfo.vehicles.map(vehicle => (
        <Card key={vehicle.id} sx={{ maxWidth: 275, backgroundColor: 'lightgray' }}>
          <CardContent sx={{ pb: 0 }}>
            <Typography component='div' variant='caption' sx={{ fontSize: 14 }} color="text.secondary" gutterBottom>
              {vehicle.short_description_with_color}
            </Typography>
            <Typography component='div' variant='caption' sx={{ width: '100%', mb: 1 }}>
              {vehicle.license ? 'License # ' + vehicle.license : 'No license recorded'}
            </Typography>
            <Typography component='div' variant='caption' sx={{ width: '100%', mb: 1 }}>
              {vehicle.vin ? 'VIN # ' + vehicle.vin : 'No VIN recorded'}
            </Typography>
            <Typography component='div' variant='caption' sx={{ width: '100%', mb: 0 }}>
              {vehicle.last_mileage ? 'Recorded Mileage: ' + formatMileage(vehicle.last_mileage) : 'No mileage recorded'}
            </Typography>
          </CardContent>
          <CardActions>
            <Button size="small" onClick={() => handleOpenService(vehicle.id)}>Add Service</Button>
            <Button component={Link} to={'/vehicles/' + vehicle.id} size="small">View Vehicle</Button>
          </CardActions>
        </Card>
       ))}
      </Box>
    </Box>
  );
}